import type { CoreLogger } from '@elara/core/logger';

import type { MessageData, MessagePart } from '#schemas/message-schemas.js';
import type { MessageResult } from '../messages/MessageProcessor.js';
import type { ProcessorContext } from '../types.js';

import { MessageProcessor } from '../messages/MessageProcessor.js';

/**
 * Abstract base class for part-level processors.
 *
 * Part processors are the granular building blocks that transform
 * individual message parts. They can be used standalone or composed
 * into message-level processors via PartProcessorAdapter.
 *
 * @example
 * ```typescript
 * class FilterWidgetPartsProcessor extends PartProcessor {
 *   readonly name = 'FilterWidgetParts';
 *
 *   async process(part: MessagePart, context: ProcessorContext): Promise<PartResult> {
 *     if (isWidgetPart(part)) {
 *       return { parts: [] }; // Filter out
 *     }
 *     return { parts: [part] }; // Pass through
 *   }
 * }
 * ```
 */
export abstract class PartProcessor {
  /** Processor name for logging and debugging */
  abstract readonly name: string;
  _logger: CoreLogger | undefined;

  constructor(protected readonly baseLogger: CoreLogger) {}

  get logger(): CoreLogger {
    this._logger ??= this.baseLogger.child({ name: this.name });
    return this._logger!;
  }
  /**
   * Process a single message part.
   *
   * @param part - The message part to process
   * @param context - Processing context with pipeline info and dependencies
   * @returns Processing result (always async for consistency)
   */
  abstract process(part: MessagePart, context: ProcessorContext): Promise<PartResult> | PartResult;

  processSync(part: MessagePart, context: ProcessorContext): PartResult {
    const result = this.process(part, context);
    if (result instanceof Promise) {
      throw new TypeError('processSync is not supported for processors that return a Promise');
    }
    return result;
  }
}

/**
 * Chains multiple part processors together.
 * Parts flow through each processor in sequence, with support for
 * 1:N transformations and stopPropagation.
 */
export class ChainPartProcessor extends PartProcessor {
  static from(processors: readonly PartProcessor[], baseLogger: CoreLogger): ChainPartProcessor {
    return new ChainPartProcessor(processors, baseLogger);
  }

  readonly name = 'ChainPartProcessor';

  constructor(
    private readonly processors: readonly PartProcessor[],
    baseLogger: CoreLogger,
  ) {
    super(baseLogger);
  }

  async process(part: MessagePart, context: ProcessorContext): Promise<PartResult> {
    if (this.processors.length === 0) return { parts: [part] };

    let parts: MessagePart[] = [part];
    let propagationStopped = false;

    for (const processor of this.processors) {
      const nextParts: MessagePart[] = [];
      let shouldStop = false;

      for (const p of parts) {
        const result = await processor.process(p, context);
        nextParts.push(...result.parts);
        if (result.stopPropagation) {
          shouldStop = true;
          propagationStopped = true;
        }
      }
      parts = nextParts;
      if (shouldStop || parts.length === 0) break;
    }

    return propagationStopped ? { parts, stopPropagation: true } : { parts };
  }

  /**
   * Synchronous version of process() for use in contexts that cannot be async
   * (e.g., Firestore data converters on the client).
   *
   * Calls processSync on each processor in the chain.
   * Will throw if any processor returns a Promise.
   */
  override processSync(part: MessagePart, context: ProcessorContext): PartResult {
    if (this.processors.length === 0) return { parts: [part] };
    let parts: MessagePart[] = [part];
    let propagationStopped = false;
    for (const processor of this.processors) {
      const nextParts: MessagePart[] = [];
      let shouldStop = false;
      for (const p of parts) {
        const result = processor.processSync(p, context);
        nextParts.push(...result.parts);
        if (result.stopPropagation) {
          shouldStop = true;
          propagationStopped = true;
        }
      }
      parts = nextParts;
      if (shouldStop || parts.length === 0) break;
    }
    return propagationStopped ? { parts, stopPropagation: true } : { parts };
  }
}

/**
 * Adapter that applies a PartProcessor to all parts in a message.
 * Bridges part-level and message-level processing.
 */
export class PartProcessorAdapter extends MessageProcessor {
  readonly name: string;

  constructor(
    private readonly partProcessor: PartProcessor,
    baseLogger: CoreLogger,
  ) {
    super(baseLogger);
    this.name = partProcessor.name;
  }

  async process(message: MessageData, context: ProcessorContext): Promise<MessageResult> {
    if (!message.content || message.content.length === 0) return { messages: [message] };
    const partContext: ProcessorContext = {
      ...context,
      message: { role: message.role, metadata: message.metadata ?? {} },
    };
    const transformedContent: MessagePart[] = [];
    for (const part of message.content) {
      const result = await this.partProcessor.process(part, partContext);
      transformedContent.push(...result.parts);
    }
    return { messages: [{ ...message, content: transformedContent }] };
  }
}

/**
 * Result of processing a single part.
 * Supports 1:0, 1:1, and 1:N transformations.
 */
export interface PartResult {
  /**
   * The transformed part(s) to emit.
   * - Empty array: part is filtered out (1:0)
   * - Single element: 1:1 transformation
   * - Multiple elements: 1:N expansion
   */
  parts: MessagePart[];

  /**
   * If true, stop processing this part through remaining processors.
   * The parts in `parts` will still be emitted.
   */
  stopPropagation?: boolean;
}
